import type { ICheckoutPaymentMethod, IPaymentMethod } from '../customer.dto';
import type { ICreateChargeResponse } from '../order.dto';
import type { OrderSource } from '../order.dto';

/**
 * Concrete payment processors that can be selected for an organization.
 * Future processors (for example Whop) extend this union when implemented.
 */
export type PaymentProcessorId = 'stripe' | 'nmi' | 'whop';

export type PaymentProcessorImplementationId =
  | PaymentProcessorId
  | 'legacy-stripe';

/**
 * Organization-level payment routing.
 * - missing / `legacy` + stripeEnabled → existing PaymentService (Stripe)
 * - missing / `legacy` without stripeEnabled, or `nmi` → PaymentGatewayService + NmiProcessor
 * - `stripe` → PaymentGatewayService + StripeProcessor
 */
export type OrgPaymentGateway = 'legacy' | PaymentProcessorId;

export interface PaymentRoutingConfig {
  paymentGateway?: OrgPaymentGateway;
  stripeEnabled?: boolean;
}

/**
 * Resolves the processor recorded on new payment entities.
 *
 * This is shared by backend services and frontend checkout routing. Do not
 * infer the processor from public keys; they are credentials, not routing
 * configuration.
 */
export function resolvePaymentProcessor(
  config: PaymentRoutingConfig,
  entityProcessor?: PaymentProcessorId
): PaymentProcessorId {
  if (entityProcessor) {
    return entityProcessor;
  }

  if (config.paymentGateway && config.paymentGateway !== 'legacy') {
    return config.paymentGateway;
  }

  return config.stripeEnabled ? 'stripe' : 'nmi';
}

/**
 * Resolves the concrete implementation, preserving the frozen legacy Stripe
 * path for legacy organizations with Stripe enabled.
 */
export function resolvePaymentProcessorImplementation(
  config: PaymentRoutingConfig,
  entityProcessor?: PaymentProcessorId
): PaymentProcessorImplementationId {
  const configuredGateway =
    config.paymentGateway && config.paymentGateway !== 'legacy'
      ? config.paymentGateway
      : undefined;

  if (configuredGateway) {
    return entityProcessor ?? configuredGateway;
  }

  if (entityProcessor) {
    return entityProcessor === 'stripe' && config.stripeEnabled
      ? 'legacy-stripe'
      : entityProcessor;
  }

  return config.stripeEnabled ? 'legacy-stripe' : 'nmi';
}

export type PaymentCurrency = 'USD' | 'CAD';

export type PaymentMetadataValue = string | number | boolean | null;

export type PaymentMetadata = Record<string, PaymentMetadataValue>;

/**
 * Org-derived values pushed into adapters by PaymentGatewayService.
 * Adapters must not load organization config themselves.
 */
export interface PaymentGatewayContext {
  organization: string;
  currency: PaymentCurrency;
  applicationFeesPercentage?: number;
  statementDescriptor?: string;
}

export enum ChargeType {
  MANUAL_ORDER = 'manual_order',
  CUSTOMER_RETRY = 'customer_retry',
  MANUAL_RETRY = 'manual_retry',
  SUBSCRIPTION_CYCLE = 'subscription_cycle',
  CHECKOUT_ORDER = 'checkout_order',
  DELAYED_CAPTURE = 'delayed_capture'
}

export type ICreateCharge = {
  resourceId: number | string;
  gatewayOrderId?: number | string;
  orderDescription?: string;
  subscriptionId?: string;
  source?: string;
  resourceType: 'orders' | 'appointments';
  amount: number;
  customerId: string;
  paymentMethod: ICheckoutPaymentMethod;
  type: ChargeType;
  /** @deprecated use type instead */
  offSession?: boolean;
  /** @deprecated use type instead */
  merchantInitiated?: boolean;
  initialTransactionId?: string;
  /**
   * Extra metadata stamped on the vendor payment. Whop checkout-with-saved-card
   * puts `cartId` here so an async bank charge can still create orders via webhook.
   */
  metadata?: PaymentIntentMetadata;
};

export type PaymentGatewayChargeInput = ICreateCharge;

export type PaymentGatewayChargeResult = ICreateChargeResponse;

export interface PaymentGatewayNextAction {
  type: string;
  [key: string]: unknown;
}

/**
 * Caller-compatible intent shape used by checkout/orders/subscriptions.
 * Preserves snake_case fields that existing call sites already read.
 */
export interface PaymentGatewayIntentResult {
  id: string;
  object: 'payment_intent' | 'setup_intent';
  status: string;
  amount?: number;
  currency?: string;
  client_secret: string | null;
  next_action?: PaymentGatewayNextAction | null;
  metadata?: PaymentMetadata;
}

export type PaymentIntentResult = PaymentGatewayIntentResult & {
  object: 'payment_intent';
};

export type SetupIntentResult = PaymentGatewayIntentResult & {
  object: 'setup_intent';
};

export type PaymentIntentMetadata = {
  source?: OrderSource | 'manual_retry' | 'customer_retry' | 'delayed_capture';
  resourceId?: string;
  orderId?: string;
  resourceType?: 'orders' | 'appointments';
  cartId?: string;
  subscriptionId?: string;
  shippingAddress1?: string;
  shippingAddress2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  shippingCountry?: string;
  couponCode?: string;
  giftCardCode?: string;
  pharmacyAddressName?: string;
  pharmacyAddress1?: string;
  pharmacyAddress2?: string;
  pharmacyCity?: string;
  pharmacyState?: string;
  pharmacyPostalCode?: string;
  pharmacyCountry?: string;
  pharmacyPhoneNumber?: string;
  pharmacyFaxNumber?: string;
  transferOut?: string;
  asyncActionRequired?: string;
  [key: string]: PaymentMetadataValue | undefined;
};

export interface CreatePaymentIntentInput {
  type: ChargeType;
  amount: number;
  description?: string;
  vaultId?: string;
  metadata?: PaymentIntentMetadata;
  paymentMethodId?: string;
  returnUrl?: string;
  confirmationToken?: string;
  saveToVaultId?: string;
  captureMethod?: 'automatic' | 'manual';
  confirm?: boolean;
}

export interface CreateSetupIntentInput {
  paymentMethodId?: string;
  confirmationToken?: string;
  vaultId?: string;
  description?: string;
  metadata?: PaymentIntentMetadata;
  confirm?: boolean;
  returnUrl?: string;
  saveToVaultId?: string;
}

export interface GetIntentInput {
  intentId: string;
  intentType?: 'payment' | 'setup';
}

export interface ConfirmIntentInput {
  intentId: string;
  paymentMethodId?: string;
  returnUrl?: string;
  intentType?: 'payment' | 'setup';
}

export interface CancelIntentInput {
  intentId: string;
  intentType?: 'payment' | 'setup';
}

export interface UpdateIntentInput {
  intentId: string;
  amount?: number;
  description?: string;
  metadata?: PaymentIntentMetadata;
  paymentMethodId?: string;
  intentType?: 'payment' | 'setup';
}

export interface CapturePaymentIntentInput {
  intentId: string;
  amount?: number;
}

export interface RefundPaymentInput {
  paymentId: string;
  amount?: number;
  reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer';
}

export interface GetRefundsInput {
  paymentId: string;
}

export interface GetPaymentIntentsByOrderIdInput {
  orderId: string;
  customerId?: string;
}

export interface CancelAllOrderPaymentsInput {
  order: {
    id: string;
    cartId?: string;
    payment?: {
      paymentId?: string;
      gatewayOrderId?: string;
      processorPaymentId?: string;
      processorOrderId?: string;
      processorCustomerId?: string;
    };
  };
  customer: {
    id: string;
    stripeId?: string;
  };
}

export interface CancelAllOrderPaymentsResult {
  voidedInvoiceIds: string[];
  canceledPaymentIntentIds: string[];
}

export interface PaymentGatewayRefundResult {
  id: string;
  status: string;
  amount?: number;
  currency?: string;
  paymentId?: string;
  gatewayTransactionId?: string;
  reason?: string | null;
  createdAt?: Date | null;
}

export type RefundResult = PaymentGatewayRefundResult;

export interface VaultCustomerInput {
  vaultId: string;
}

export interface PaymentMethodIdInput {
  paymentMethodId: string;
}

export interface AttachDefaultPaymentMethodInput {
  vaultId: string;
  paymentMethodToken: string;
  zip?: string;
}

export interface RemovePaymentMethodInput {
  paymentMethodId: string;
  vaultId?: string;
}

export interface SetDefaultPaymentMethodInput {
  vaultId: string;
  paymentMethodId: string;
}

export interface PaymentMethodMutationResult {
  success: boolean;
}

/**
 * Processor-neutral payment operations.
 * Implementations map these contracts onto Stripe/NMI SDKs and never expose
 * provider-specific response types to callers.
 */
export interface PaymentProcessor {
  readonly processor: PaymentProcessorImplementationId;

  createCharge(
    context: PaymentGatewayContext,
    input: PaymentGatewayChargeInput
  ): Promise<PaymentGatewayChargeResult>;

  createPaymentIntent(
    context: PaymentGatewayContext,
    input: CreatePaymentIntentInput
  ): Promise<PaymentIntentResult>;

  createSetupIntent(
    context: PaymentGatewayContext,
    input: CreateSetupIntentInput
  ): Promise<SetupIntentResult>;

  getIntent(
    context: PaymentGatewayContext,
    input: GetIntentInput
  ): Promise<PaymentGatewayIntentResult | null>;

  confirmIntent(
    context: PaymentGatewayContext,
    input: ConfirmIntentInput
  ): Promise<PaymentGatewayIntentResult>;

  cancelIntent(
    context: PaymentGatewayContext,
    input: CancelIntentInput
  ): Promise<PaymentGatewayIntentResult>;

  updateIntent(
    context: PaymentGatewayContext,
    input: UpdateIntentInput
  ): Promise<PaymentGatewayIntentResult | null>;

  capturePaymentIntent(
    context: PaymentGatewayContext,
    input: CapturePaymentIntentInput
  ): Promise<PaymentIntentResult>;

  refundPayment(
    context: PaymentGatewayContext,
    input: RefundPaymentInput
  ): Promise<PaymentGatewayRefundResult>;

  getRefunds(
    context: PaymentGatewayContext,
    input: GetRefundsInput
  ): Promise<PaymentGatewayRefundResult[]>;

  getPaymentIntentsByOrderId(
    context: PaymentGatewayContext,
    input: GetPaymentIntentsByOrderIdInput
  ): Promise<PaymentIntentResult[]>;

  cancelAllOrderPayments(
    context: PaymentGatewayContext,
    input: CancelAllOrderPaymentsInput
  ): Promise<CancelAllOrderPaymentsResult>;

  getPaymentMethods(
    context: PaymentGatewayContext,
    input: VaultCustomerInput
  ): Promise<IPaymentMethod[]>;

  getPaymentMethodById(
    context: PaymentGatewayContext,
    input: PaymentMethodIdInput
  ): Promise<IPaymentMethod | null>;

  attachDefaultPaymentMethod(
    context: PaymentGatewayContext,
    input: AttachDefaultPaymentMethodInput
  ): Promise<IPaymentMethod>;

  removePaymentMethod(
    context: PaymentGatewayContext,
    input: RemovePaymentMethodInput
  ): Promise<PaymentMethodMutationResult>;

  setDefaultPaymentMethod(
    context: PaymentGatewayContext,
    input: SetDefaultPaymentMethodInput
  ): Promise<PaymentMethodMutationResult>;

  getCustomerPaymentMethodId(
    context: PaymentGatewayContext,
    input: VaultCustomerInput
  ): Promise<string | null>;

}
